import axiosClient from "@/services/shared/api/axios-client";
import config from "@/config/config";
import { ProfileUpdateFormData } from "@/pages/Settings/schemas";

export interface StudentProfile {
  id: string;
  name: string;
  email: string;
  phone?: string;
  street?: string;
  number?: string;
  zipCode?: string;
  neighborhood?: string;
  city?: string;
  state?: string;
  country?: string;
  photoFilename?: string;
}

export interface ProfilePhotoUploadResponse {
  photoFilename: string;
}

const API_URL = "/students/profile";

// Real API implementation
const apiGetProfile = async (): Promise<StudentProfile> => {
  const response = await axiosClient.get<StudentProfile>(API_URL);
  return response.data;
};

const apiUpdateProfile = async (data: ProfileUpdateFormData & { email?: string }): Promise<void> => {
  await axiosClient.put(API_URL, {
    name: data.name,
    email: data.email || '', // Email is required by backend
    phone: data.phone || null,
    street: data.street || null,
    number: data.number || null,
    zipCode: data.zipCode || null,
    neighborhood: data.neighborhood || null,
    city: data.city || null,
    state: data.state || null,
    country: data.country || null,
    photoFilename: data.photoFilename || null,
  });
};

const apiUploadProfilePhoto = async (file: File): Promise<ProfilePhotoUploadResponse> => {
  const formData = new FormData();
  formData.append("photo", file);

  const response = await axiosClient.post<ProfilePhotoUploadResponse>(
    `${API_URL}/photo`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );

  return response.data;
};

// Mock implementations
const mockGetProfile = async (): Promise<StudentProfile> => {
  return {
    id: "student-1",
    name: "João Silva",
    email: "student@example.com",
    phone: "(11) 99999-9999",
    street: "Rua das Flores",
    number: "123",
    zipCode: "01234-567",
    neighborhood: "Centro",
    city: "São Paulo",
    state: "SP",
    country: "Brasil",
    photoFilename: null,
  };
};

const mockUpdateProfile = async (data: ProfileUpdateFormData): Promise<void> => {
  console.log("Mock: Updating profile", data);
  await new Promise((resolve) => setTimeout(resolve, 500));
};

const mockUploadProfilePhoto = async (file: File): Promise<ProfilePhotoUploadResponse> => {
  console.log("Mock: Uploading profile photo", file.name);
  await new Promise((resolve) => setTimeout(resolve, 1000));
  return {
    photoFilename: `mock-${Date.now()}-${file.name}`,
  };
};

// Export services that select between real and mock implementation
export const getProfile = config.selectApi(apiGetProfile, mockGetProfile);
export const updateProfile = config.selectApi(apiUpdateProfile, mockUpdateProfile);
export const uploadProfilePhoto = config.selectApi(apiUploadProfilePhoto, mockUploadProfilePhoto);
